import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, AlertCircle, Bell, Truck, Wrench, MapPin, ChevronRight, X } from 'lucide-react';

/**
 * Daily Reminders popup.
 *
 * Visibility rules (clearer / harder to miss than before):
 *  • Opens on first login of the day (existing behaviour).
 *  • If a user is already signed in across midnight, a 1-minute timer
 *    re-evaluates "is it today + after 9am locally?" and pops the dialog
 *    automatically at 09:00 (or whenever they next have the tab open after that).
 *  • Must be manually dismissed — there is NO backdrop-click-to-close. The
 *    only close action is the explicit "Dismiss for today" button.
 *  • Dismissal is persisted in localStorage per (user, date). Any stale keys
 *    for previous days are cleaned up on mount so a long-running session
 *    can never block today's popup with yesterday's flag.
 */
const STORAGE_PREFIX = 'daily-reminders-shown:';
const POPUP_HOUR = 9;        // 9am local time
const POLL_MS = 60 * 1000;   // re-check every minute

function localDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function DailyRemindersPopup() {
  const { user, token, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [todayKey, setTodayKey] = useState(() => localDateKey());
  const lastFetchedKey = useRef(null);

  // Clean up any old "daily-reminders-shown:<userId>:<old-date>" keys so a
  // stale dismissal from yesterday can never block today's popup.
  useEffect(() => {
    if (!user?.id) return;
    const today = localDateKey();
    const prefix = `${STORAGE_PREFIX}${user.id}:`;
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix) && !k.endsWith(today)) {
          toRemove.push(k);
        }
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch { /* localStorage may be disabled in private mode — non-fatal */ }
  }, [user?.id]);

  // Tick every minute: rotate the date key at midnight, and re-attempt the
  // popup once it crosses 9am if the user has been signed in all morning.
  useEffect(() => {
    const tick = () => setTodayKey(localDateKey());
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, []);

  const storageKey = useMemo(
    () => (user?.id ? `${STORAGE_PREFIX}${user.id}:${todayKey}` : null),
    [user?.id, todayKey]
  );

  const tryShow = useCallback(async () => {
    if (!user || !token || !storageKey) return;
    if (user.role === 'trade' || user.role === 'demo' || user.is_demo) return;
    if (localStorage.getItem(storageKey) === '1') return;

    // Don't re-fetch within the same key during this session unless dismissed.
    if (lastFetchedKey.current === storageKey && open) return;

    // Hold the popup until 9am local. (If the page is first opened AFTER 9am,
    // popping immediately on mount is the desired behaviour.)
    const now = new Date();
    if (now.getHours() < POPUP_HOUR) return;

    lastFetchedKey.current = storageKey;
    try {
      const res = await axios.get(`${API}/reminders/daily`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = res.data || { today: [], upcoming_collections: [] };
      setData(payload);
      const count = (payload.today?.length || 0) + (payload.upcoming_collections?.length || 0);
      if (count > 0) {
        setOpen(true);
      } else {
        // Nothing today — mark as shown so we don't repeatedly hit the API.
        localStorage.setItem(storageKey, '1');
      }
    } catch {
      /* silent — never block the rest of the app on a reminders failure */
    }
  }, [user, token, API, storageKey, open]);

  // Trigger an attempt whenever the (user, date) key changes — covers initial
  // mount, midnight rollover, and the 9am rule above.
  useEffect(() => {
    tryShow();
  }, [tryShow]);

  const dismiss = () => {
    if (storageKey) localStorage.setItem(storageKey, '1');
    setOpen(false);
  };

  if (!user || !data) return null;

  const today = data.today || [];
  const upcoming = data.upcoming_collections || [];

  const iconFor = (t) => {
    switch (t) {
      case 'memorial_collection':
        return <Truck className="w-4 h-4 text-amber-400" />;
      case 'appointment':
        return <Calendar className="w-4 h-4 text-purple-400" />;
      case 'job_booking':
      case 'fitting':
        return <Wrench className="w-4 h-4 text-emerald-400" />;
      case 'delivery':
      case 'shipment_eta':
        return <Truck className="w-4 h-4 text-blue-400" />;
      case 'task':
        return <Bell className="w-4 h-4 text-slate-300" />;
      default:
        return <Calendar className="w-4 h-4 text-slate-300" />;
    }
  };

  const labelFor = (t) =>
    ({
      memorial_collection: 'Collection',
      appointment: 'Appointment',
      job_booking: 'Job booking',
      fitting: 'Fitting',
      delivery: 'Delivery',
      shipment_eta: 'Shipment',
      task: 'Task',
      custom: 'Event',
    }[t] || 'Event');

  const goToItem = (item) => {
    if (item.order_id) {
      navigate(`/orders/${item.order_id}`);
      dismiss();
    } else {
      navigate('/tasks');
      dismiss();
    }
  };

  const bucketLabel = (days) => {
    if (days === 1) return 'Tomorrow';
    if (days === 3) return 'In 3 days';
    if (days === 7) return 'In 1 week';
    return `In ${days} day${days === 1 ? '' : 's'}`;
  };

  const bucketColour = (days) => {
    if (days === 1) return 'bg-red-900/40 text-red-200 border-red-700';
    if (days === 3) return 'bg-amber-900/40 text-amber-200 border-amber-700';
    return 'bg-blue-900/40 text-blue-200 border-blue-700';
  };

  // We intentionally do NOT pass `onOpenChange={dismiss}` — the user must use
  // the explicit "Dismiss for today" button to acknowledge they've seen it.
  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) setOpen(true); /* swallow auto-close */ }}>
      <DialogContent
        className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl"
        data-testid="daily-reminders-popup"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-fuchsia-400" />
            Today's Schedule &amp; Upcoming Collections
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {today.length > 0 || upcoming.length > 0
              ? "Here's what's coming up for the whole team. Click \"Dismiss for today\" to close."
              : 'Nothing scheduled.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
          {/* Today */}
          <section data-testid="reminders-today-section">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-slate-300">Today</h3>
              <Badge className="bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-700">{today.length}</Badge>
            </div>
            {today.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nothing on the diary for today.</p>
            ) : (
              <ul className="space-y-2">
                {today.map((it) => (
                  <li
                    key={`t-${it.source}-${it.id}`}
                    className="flex items-start gap-3 px-3 py-2 bg-slate-800/60 border border-slate-700 rounded-lg hover:bg-slate-800 cursor-pointer transition"
                    onClick={() => goToItem(it)}
                    data-testid={`reminder-today-${it.id}`}
                  >
                    <div className="mt-0.5">{iconFor(it.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wider text-slate-400">{labelFor(it.type)}</span>
                        {it.assigned_to && <span className="text-[10px] text-slate-500">· {it.assigned_to}</span>}
                      </div>
                      <div className="text-sm font-medium text-white truncate">{it.title}</div>
                      {it.location && (
                        <div className="text-xs text-slate-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {it.location}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 mt-1.5" />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Upcoming collections */}
          <section data-testid="reminders-upcoming-section">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-semibold tracking-wide uppercase text-slate-300">Upcoming collections</h3>
              <Badge className="bg-amber-900/40 text-amber-200 border-amber-700">{upcoming.length}</Badge>
            </div>
            {upcoming.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No collections in the next 7 days.</p>
            ) : (
              <ul className="space-y-2">
                {upcoming.map((it) => (
                  <li
                    key={`u-${it.source}-${it.id}`}
                    className="flex items-start gap-3 px-3 py-2 bg-slate-800/60 border border-slate-700 rounded-lg hover:bg-slate-800 cursor-pointer transition"
                    onClick={() => goToItem(it)}
                    data-testid={`reminder-upcoming-${it.id}`}
                  >
                    <div className="mt-0.5">{iconFor(it.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[10px] ${bucketColour(it.days_until)}`}>{bucketLabel(it.days_until)}</Badge>
                        <span className="text-xs text-slate-400">{it.date}</span>
                      </div>
                      <div className="text-sm font-medium text-white truncate">{it.title}</div>
                      {it.location && (
                        <div className="text-xs text-slate-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {it.location}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 mt-1.5" />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <DialogFooter className="border-t border-slate-800 pt-3">
          <Button
            variant="outline"
            onClick={dismiss}
            className="border-slate-600 text-slate-200 hover:bg-slate-700"
            data-testid="dismiss-daily-reminders-btn"
          >
            <X className="w-4 h-4 mr-2" /> Dismiss for today
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
